import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Shield, Users, Target, Award, Building2, Globe, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO, { organizationSchema, createBreadcrumbSchema } from "../components/SEO";

export default function SobrePage() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'pt';

  // Language-aware paths
  const paths = {
    home: '/',
    privacy: currentLang === 'en' ? '/en/privacy-policy' : '/pt/politica-de-privacidade',
    terms: currentLang === 'en' ? '/en/terms-of-use' : '/pt/termos-de-uso',
    contact: currentLang === 'en' ? '/en/contact' : '/pt/contato',
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // SEO Configuration
  const seoConfig = currentLang === 'en' ? {
    title: "About Us - OmbudNow",
    description: "Learn about OmbudNow, the leading whistleblowing and ombudsman platform for companies.",
    canonical: "/en/about",
    keywords: "about ombudnow, whistleblowing platform, corporate ethics"
  } : {
    title: "Sobre Nós - OmbudNow",
    description: "Conheça a OmbudNow, a plataforma líder em canal de denúncias e ouvidoria para empresas.",
    canonical: "/pt/sobre",
    keywords: "sobre ombudnow, plataforma de denúncias, ética corporativa"
  };

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: currentLang === 'en' ? 'Home' : 'Início', url: '/' },
    { name: currentLang === 'en' ? 'About' : 'Sobre', url: seoConfig.canonical }
  ]);

  const content = currentLang === 'en' ? {
    back: "Back",
    title: "About OmbudNow",
    subtitle: "We are on a mission to make corporate ethics accessible to businesses of all sizes.",
    mission: {
      title: "Our Mission",
      description: "To democratize access to whistleblowing and ombudsman tools, enabling small and medium companies to build a culture of ethics and transparency."
    },
    values: {
      title: "Our Values",
      description: "Integrity, transparency, and innovation guide everything we do. We believe that every company deserves tools to promote an ethical environment."
    },
    vision: {
      title: "Our Vision",
      description: "A world where every company, regardless of size, has the resources to identify and resolve internal issues before they become crises."
    },
    whyTitle: "Why OmbudNow?",
    whyDescription: "We created OmbudNow because we believe that small and medium companies shouldn't be left behind when it comes to corporate compliance and ethics. Our platform was designed to be simple, affordable, and effective.",
    features: [
      "Setup in less than 5 minutes",
      "No specialized technical knowledge required",
      "Affordable plans for companies of all sizes",
      "Full compliance with data protection regulations"
    ],
    ctaTitle: "Ready to get started?",
    ctaButton: "Create my free channel"
  } : {
    back: "Voltar",
    title: "Sobre a OmbudNow",
    subtitle: "Estamos em uma missão para tornar a ética corporativa acessível para empresas de todos os tamanhos.",
    mission: {
      title: "Nossa Missão",
      description: "Democratizar o acesso a ferramentas de canal de denúncias e ouvidoria, permitindo que pequenas e médias empresas construam uma cultura de ética e transparência."
    },
    values: {
      title: "Nossos Valores",
      description: "Integridade, transparência e inovação guiam tudo o que fazemos. Acreditamos que toda empresa merece ferramentas para promover um ambiente ético."
    },
    vision: {
      title: "Nossa Visão",
      description: "Um mundo onde toda empresa, independentemente do tamanho, tenha os recursos para identificar e resolver problemas internos antes que se tornem crises."
    },
    whyTitle: "Por que a OmbudNow?",
    whyDescription: "Criamos a OmbudNow porque acreditamos que pequenas e médias empresas não devem ficar para trás quando o assunto é compliance e ética corporativa. Nossa plataforma foi projetada para ser simples, acessível e eficaz.",
    features: [
      "Configuração em menos de 5 minutos",
      "Não requer conhecimento técnico especializado",
      "Planos acessíveis para empresas de todos os tamanhos",
      "Total conformidade com regulamentações de proteção de dados"
    ],
    ctaTitle: "Pronto para começar?",
    ctaButton: "Criar meu canal gratuito"
  };

  return (
    <div className="min-h-screen bg-white font-ibm">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        canonical={seoConfig.canonical}
        lang={currentLang}
        keywords={seoConfig.keywords}
        schemas={[organizationSchema, breadcrumbSchema]}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={paths.home} className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-900 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-slate-900 font-chivo tracking-tight">OMBUDNOW</span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to={currentLang === 'en' ? '/en/about' : '/pt/sobre'} className="text-slate-900 font-medium text-sm">
                {currentLang === 'en' ? 'About' : 'Sobre'}
              </Link>
              <Link to={currentLang === 'en' ? '/en/blog' : '/pt/blog'} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                Blog
              </Link>
              <Link to={currentLang === 'en' ? '/en/contact' : '/pt/contato'} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                {currentLang === 'en' ? 'Contact' : 'Contato'}
              </Link>
              <div className="flex items-center gap-1.5 text-slate-500 text-sm border-l border-slate-200 pl-6 ml-2">
                <Globe className="w-4 h-4" />
                <button 
                  onClick={() => changeLanguage('pt')} 
                  className={`px-1 ${currentLang === 'pt' ? 'text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'} transition-colors`}
                >
                  PT
                </button>
                <span className="text-slate-300">/</span>
                <button 
                  onClick={() => changeLanguage('en')} 
                  className={`px-1 ${currentLang === 'en' ? 'text-slate-900 font-semibold' : 'text-slate-500 hover:text-slate-700'} transition-colors`}
                >
                  EN
                </button>
              </div>
            </nav>

            <Button
              className="bg-slate-900 text-white hover:bg-slate-800 font-medium px-5"
              onClick={() => window.location.href = '/onboarding?plan=free'}
            >
              {currentLang === 'en' ? 'Start free' : 'Começar grátis'}
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 font-chivo tracking-tight">
              {content.title}
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              {content.subtitle}
            </p>
          </div>
        </section>

        {/* Mission, Values, Vision Section */}
        <section className="py-16 px-6 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-8 border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-200 transition-all">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-chivo">{content.mission.title}</h3>
                <p className="text-slate-600 leading-relaxed">
                  {content.mission.description}
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-200 transition-all">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-6">
                  <Award className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-chivo">{content.values.title}</h3>
                <p className="text-slate-600 leading-relaxed">
                  {content.values.description}
                </p>
              </div>

              <div className="bg-white rounded-xl p-8 border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-200 transition-all">
                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center mb-6">
                  <Users className="w-6 h-6 text-violet-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 font-chivo">{content.vision.title}</h3>
                <p className="text-slate-600 leading-relaxed">
                  {content.vision.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why OmbudNow Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 font-chivo tracking-tight text-center">
              {content.whyTitle}
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed text-center mb-12">
              {content.whyDescription}
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {content.features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-slate-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6 bg-gradient-to-r from-blue-600 to-blue-700">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 font-chivo tracking-tight">
              {content.ctaTitle}
            </h2>
            <Button
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50 font-medium px-8 py-6 text-base"
              onClick={() => window.location.href = '/onboarding?plan=free'}
            >
              {content.ctaButton}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#0B0F19] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white font-chivo">OMBUDNOW</span>
            </div>
            <p className="text-slate-500 text-sm">
              © 2025 OmbudNow. {currentLang === 'en' ? 'All rights reserved.' : 'Todos os direitos reservados.'}
            </p>
            <div className="flex items-center gap-6">
              <Link to={paths.privacy} className="text-slate-500 hover:text-white text-sm transition-colors">
                {currentLang === 'en' ? 'Privacy' : 'Privacidade'}
              </Link>
              <Link to={paths.terms} className="text-slate-500 hover:text-white text-sm transition-colors">
                {currentLang === 'en' ? 'Terms' : 'Termos'}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
